import { Component, Inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import { MailDataObj, TownshipInfo, TownshipPublicSettings, VoucherGroup } from '../interfaces';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  ValidatorFn,
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { VerifyPhoneComponent } from './dialogs/verify-phone/verify-phone.component';
import moment from 'moment';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { SucceedActivationComponent } from './dialogs/succeed-activation/succeed-activation.component';
import {
  getCurrencyString,
  getEnvName,
  getHeaderImageUrl,
  getTownshipInfo,
  getTermsUrl,
  updateTheme,
  getTownshipImage,
  decodeRouteParam,
  encodeRouteParam,
} from '../globals';
import { db, functions } from '../app.component';
import { PublicFunctionsService } from '../services/public-functions.service';
import { CustomValidators } from 'angular-custom-validators';
import { httpsCallable } from 'firebase/functions';
import { NgStyle, NgClass, DatePipe, KeyValuePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
/**
 * Configuration data passed to the ActivateVoucherComponent.
 * This can be provided via component `@Input` in embedded scenarios or via `MAT_DIALOG_DATA` in dialog scenarios.
 */
export interface ActivateVoucherComponentData {
  /** The Firestore document ID of the targeted municipality (township). Used across all flows. */
  townshipId: string;
  /** The Firestore document ID of the targeted voucher group. Used across all flows except when direct code activation occurs. */
  voucherGroupId: string;
  /** Pre-filled citizen details (e.g. address, email) to populate the form dynamically and lock them from changes. */
  userData: PreFilledUserData;
  /** Custom CSS width for the form container (e.g., '590px' for stepper integration). Passed in Search & Passport flows. */
  width?: string;
  /** True if terms have already been agreed to in a parent step/flow (hides the terms checkbox). Used in Search & Passport flows. */
  termsAccepted?: boolean;
  /** The sustainability target/level selected by the citizen. Passed and attached to payloads in the Home Passport flow. */
  // ambitionLevel remains in legacy data. Review removal on 2027-04-01.
  // ambitionLevel?: string;
  /** Toggled sustainable measures to bind to the issued voucher. Passed and attached to payloads in the Home Passport flow. */
  measures?: string[];
  /** True to hide stats like 'X vouchers remaining'. Used in Search & Home Passport flows. */
  noStatistics?: boolean;
  /** An identifier tag representing the source flow (e.g., 'find-voucher-groups', 'passport'). */
  activateType?: string;
  /** True if this is an administrative tool to pre-fill and email request links to citizens. Used in Admin/Prep flow. */
  preparingRequest?: boolean;
}

/**
 * Citizen data used to pre-fill the activation/request form.
 * Disables the corresponding form inputs so users cannot change pre-verified details.
 */
export interface PreFilledUserData {
  /** Pre-verified postal code. */
  postal?: string;
  /** Pre-verified house number. */
  houseNumber?: number;
  /** Pre-verified house number addition/suffix. */
  houseNumberAddition?: string;
  /** Citizen's email address. */
  email?: string;
  /** Citizen's phone number (must start with '06'). */
  phone?: string;
  /** Full name of the applicant. */
  name?: string;
  /** International Bank Account Number for cashback/reimbursement voucher groups. */
  ibanNumber?: string;
  /** Name of the bank account holder matching the IBAN. */
  bankAccountName?: string;
  /** Object containing answers to dynamically-rendered questionnaire questions. */
  questionnaire?: object;
}

@Component({
  selector: 'app-activate-voucher',
  templateUrl: './activate-voucher.component.html',
  styleUrls: ['./activate-voucher.component.scss'],
  imports: [
    NgStyle,
    MatButton,
    MatIcon,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    MatSelect,
    MatOption,
    CdkTextareaAutosize,
    MatCheckbox,
    MatDialogClose,
    MatProgressSpinner,
    DatePipe,
    KeyValuePipe,
    TranslatePipe,
  ],
})
export class ActivateVoucherComponent implements OnInit, OnDestroy {
  /** Loaded status of the component. Form/spinner renders once true. */
  loaded = signal(false);
  campaignInactive = signal(false);
  env = environment;
  /** Consolidated config data (resolved from either inputData, dialogData, or route params). */
  data: ActivateVoucherComponentData;
  /** Resolved pre-filled data applied to the controls. */
  preFilledData: PreFilledUserData = {};
  /** Input parameters passed when this component is embedded within parent templates (e.g. Search Step or Passport flows). */
  @Input() inputData: ActivateVoucherComponentData;
  /** Signal indicating if statistics/counters (like 'vouchers left') should be hidden. */
  noStatistics = signal(false);
  iframeError = signal(true);
  /** True while the component is awaiting response from Firestore/Cloud Function (shows loading animations). */
  awaitingResponse = signal(false);
  /** Township ID loaded from route params or input config. */
  townshipId = signal<string>(undefined);
  /** Municipality name (township) displayed in headers and email templates. */
  townshipName = signal<string>(undefined);
  uniqueTownshipName = signal<string>(undefined);
  voucherGroupId = signal<string>(undefined);
  termsUrl = signal<string>(getTermsUrl('general'));
  privacyUrl = signal<string>(getTermsUrl('privacy'));
  townshipInfo = signal<TownshipInfo>(undefined);
  township = signal<TownshipPublicSettings>(undefined);
  voucherGroup = signal<VoucherGroup>(undefined);
  activateVoucherForm: UntypedFormGroup;
  availableVouchers = signal<number>(undefined);
  termsRows = signal<number>(undefined);
  /** True if the component was initialized as a pop-up dialog using MatDialog. Hides backgrounds/banners, adds Close button. */
  isDialog = signal(false);
  /** True if the component is embedded inside a stepper / host component. Hides banners and layout margins. */
  isEmbedded = signal(false);
  /** True if the admin/helpdesk is preparing an email request link. Alters the submit action to email links and eases questionnaire validation. */
  isPreperationForm = signal(false);
  /** True when a voucherGroupId is not provided (standalone direct-code entry). */
  noGroupProvided = signal(false);
  firstTime = true;
  pageWidth = signal('460px');
  /** Identifies active parent flow (e.g., 'passport', 'find-voucher-groups'). */
  activateType = signal(null);
  nameRequired = signal(false);
  questionnaireEnabled = signal(false);

  environment: string;
  error = signal<string>(undefined);

  headerImg = signal<string>(undefined);
  showHeaderImg = signal(false);

  acitvateDateNotPassed = signal(false);
  usableFromDate = signal<any>(undefined);
  prefilledFormSent = signal(false);

  envName = signal<any>({});
  validDateToRequest = signal(true);

  questionnaireForm: UntypedFormGroup = new FormGroup({});
  questionnaireData = {} as any;

  townshipImage = signal<any>(undefined);

  getCurrencyString = getCurrencyString;

  highContrast = signal(false);
  readonly db = db;
  readonly functions = functions;
  private readonly subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    public dialog: MatDialog,
    private publicFunctionsService: PublicFunctionsService,
    @Inject(MAT_DIALOG_DATA) public dialogData: ActivateVoucherComponentData,
  ) {}

  async ngOnInit() {
    if (localStorage.getItem('highContrast') && localStorage.getItem('highContrast') === '1') {
      this.highContrast.set(true);
    }

    this.activateVoucherForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      emailConfirm: ['', [Validators.required, Validators.email]],
      terms: [false, Validators.requiredTrue],
    });

    if (this.inputData && this.inputData.townshipId && this.inputData.voucherGroupId) {
      console.log('this.inputData', this.inputData);
      this.data = this.inputData;
      this.isEmbedded.set(true);
      if (this.inputData.preparingRequest) {
        this.isPreperationForm.set(true);
      }
    }

    if (this.dialogData && this.dialogData.townshipId && this.dialogData.voucherGroupId) {
      this.data = this.dialogData;
      this.isDialog.set(true);
    }

    console.log('this.data', this.data);
    if (this.data && this.data.townshipId && this.data.voucherGroupId) {
      this.preFilledData = this.data.userData;
      this.townshipId.set(this.data.townshipId);
      this.voucherGroupId.set(this.data.voucherGroupId);
      this.noStatistics.set(this.data.noStatistics ?? false);
      this.activateType.set(this.data.activateType ? this.data.activateType : null);
      if (this.data.width) {
        this.pageWidth.set(this.data.width);
      }
      if (this.data.termsAccepted) {
        this.activateVoucherForm.controls.terms.setValue(true);
      }
      await this.getInfo();
    }

    this.subscriptions.add(
      this.route.params.subscribe(async (params) => {
        console.log('params', params);
        if (params.townshipName) {
          this.townshipName.set(params.townshipName);
          this.uniqueTownshipName.set(params.townshipName);
        }
        if (params.townshipId) {
          this.townshipId.set(params.townshipId);
        }
        if (params.voucherGroupId) {
          this.voucherGroupId.set(params.voucherGroupId);
        } else {
          this.noGroupProvided.set(true);
        }
        if (params.address) {
          const addressSplit = params.address.split('*');
          if (addressSplit.length < 2) {
            return;
          }
          this.preFilledData.postal = addressSplit[0];
          this.preFilledData.houseNumber = addressSplit[1];
          this.preFilledData.houseNumberAddition = addressSplit[2];
        }
        if (params.customParams) {
          let customParams = params.customParams;
          if (!params.customParams.startsWith('email')) {
            // Used to decode the param only when it's encoded, this should be done always in the future, but for now we need to support both so that it's backward compatible
            customParams = decodeRouteParam(params.customParams);
          }
          console.log('customParams', customParams);
          const customParamsSplit = customParams.split('&');
          customParamsSplit.forEach((param) => {
            const paramSplit = param.split('=');
            const key = paramSplit[0];
            const value = paramSplit[1];
            if (key == 'questionnaire') {
              this.preFilledData[key] = JSON.parse(value);
            } else {
              this.preFilledData[key] = value;
            }
          });
        }
        if (params.postal) {
          this.preFilledData.postal = params.postal;
        }
        if (params.houseNumber) {
          this.preFilledData.houseNumber = params.houseNumber;
        }
        if (params.houseNumberAddition) {
          this.preFilledData.houseNumberAddition = params.houseNumberAddition;
        }
        if (params.ibanNumber) {
          this.preFilledData.ibanNumber = params.ibanNumber;
        }
        if (params.bankAccountName) {
          this.preFilledData.bankAccountName = params.bankAccountName;
        }
        if (params.email) {
          this.preFilledData.email = params.email;
        }
        if (params.phone) {
          this.preFilledData.phone = params.phone;
        }
        if (params.name) {
          this.preFilledData.name = params.name;
        }
        console.log('this.preFilledData', this.preFilledData);
        await this.getInfo();
      }),
    );

    this.translate.setDefaultLang(environment.language);
    this.translate.use(environment.language);
  }

  verifyPhone(phone: string) {
    const dialogRef = this.dialog.open(VerifyPhoneComponent, {
      width: '350px',
      data: { townshipId: this.townshipId(), phone },
      disableClose: true,
    });
    this.subscriptions.add(
      dialogRef.afterClosed().subscribe(async (code) => {
        if (code) {
          this.submit(code !== 'skip' ? code : null);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  getError(name, form = this.activateVoucherForm) {
    const field = form.get(name);
    if (field.touched || !field.pristine) {
      let error;
      if (field.hasError('required')) {
        error = 'required';
      }
      if (field.hasError('startsWith')) {
        error = 'must_start_with_06';
      }
      if (field.hasError('number') || field.hasError('numberInput')) {
        error = name === 'houseNumber' ? 'houseNumber' : 'number';
      }
      if (field.hasError('email')) {
        error = 'email';
      }
      if (field.hasError('postalValidator')) {
        error = 'postal';
      }
      if (field.hasError('pattern') && name === 'houseNumberAddition') {
        error = 'addition';
      }
      if (field.hasError('minlength') || field.hasError('phoneValidator')) {
        error = 'phone';
      }
      if (field.hasError('ibanValidator')) {
        error = 'invalid';
      }
      const res = this.translate.instant(`errors.${error}`);
      return res as string;
    }
    return '';
  }
  async getInfo() {
    this.townshipInfo.set(
      await getTownshipInfo({
        townshipName: this.townshipName(),
        townshipId: this.townshipId(),
      }),
    );
    this.townshipId.set(this.townshipInfo().townshipId);
    this.townshipName.set(this.townshipInfo().townshipName);
    this.township.set(this.townshipInfo().township);
    this.townshipImage.set(getTownshipImage(this.township()));

    if (this.township()?.hideSource) {
      this.campaignInactive.set(true);
      return;
    }

    if (this.township().logoImageUrl) {
      this.headerImg.set(this.township().logoImageUrl);
    } else {
      this.headerImg.set(getHeaderImageUrl(this.township().voucherPrefix));
    }
    this.envName.set(getEnvName(this.township().voucherPrefix));
    this.townshipImage.set(getTownshipImage(this.township()));
    updateTheme({
      primary: this.township().primaryColor,
      secondary: this.township().accentColor,
    });

    if (this.voucherGroupId()) {
      console.log('this.voucherGroupId', this.voucherGroupId());

      const questionnaireQuestionDocs = await getDocs(
        collection(this.db, `/globalSettings/global/questionnaireQuestions`),
      );
      questionnaireQuestionDocs.forEach((questionDoc) => {
        const data = questionDoc.data();
        this.questionnaireData[data.name] = data;
        this.questionnaireForm.addControl(
          data.name,
          new UntypedFormControl(null, !this.isPreperationForm() ? Validators.required : null),
        );
        this.questionnaireForm.addControl(
          `${data.name}Input`,
          new UntypedFormControl('', !this.isPreperationForm() ? Validators.required : null),
        );
      });

      const voucherGroupRef = doc(
        this.db,
        `township/${this.townshipId()}/voucherGroups/${this.voucherGroupId()}`,
      );

      onSnapshot(voucherGroupRef, async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as VoucherGroup;
          if (data.hidden) {
            this.campaignInactive.set(true);
            return;
          }
          this.termsRows.set(5);
          console.log('data', data);
          data.id = docSnapshot.id;

          this.availableVouchers.set(data.statistics?.availableVouchers ?? 0);
          if (data.activateFromType === 'activateOnDate') {
            if (data.usableFrom.toDate() > new Date()) {
              this.usableFromDate.set(data.usableFrom.toDate());
              this.acitvateDateNotPassed.set(true);
              console.log('activate date not passed');
            }
          }

          console.log('availableVouchers', this.availableVouchers());
          if (this.availableVouchers() <= 0) {
            this.activateVoucherForm.disable();
          } else if (!this.awaitingResponse()) {
            this.activateVoucherForm.enable();
          }

          if (!this.validDateToRequest()) {
            this.activateVoucherForm.disable();
          }
          this.voucherGroup.set({ ...data });

          if (this.voucherGroup().validUntilDate) {
            // Check if you are requesting a voucher within the allowed timeframe
            const dateThatIsValid = new Date(this.voucherGroup().validUntilDate.toDate()).setDate(
              this.voucherGroup().validUntilDate.toDate().getDate() -
                (this.voucherGroup().requestCouponDays ? this.voucherGroup().requestCouponDays : 0),
            );
            console.log('dateThatIsValid', new Date(dateThatIsValid));
            if (moment(new Date()).isAfter(dateThatIsValid)) {
              this.validDateToRequest.set(false);
            }
          }

          if (!this.voucherGroup().hideHeaderImage) {
            this.showHeaderImg.set(true);
          }

          if (this.voucherGroup().requestName && !this.voucherGroup().requestIdentification) {
            this.activateVoucherForm.addControl(
              'name',
              new UntypedFormControl('', Validators.required),
            );
            this.nameRequired.set(true);
          }
          if (this.voucherGroup().requestIdentification) {
            this.activateVoucherForm.addControl(
              'identification',
              new UntypedFormControl('', Validators.required),
            );
          }
          if (this.voucherGroup().requestPhone) {
            if (this.voucherGroup().verifyPhone) {
              this.activateVoucherForm.addControl(
                'phone',
                new UntypedFormControl('06', [
                  Validators.required,
                  CustomValidators.phone,
                  startsWith('06'),
                ]),
              );
            } else {
              this.activateVoucherForm.addControl(
                'phone',
                new UntypedFormControl('06', [Validators.required, CustomValidators.phone]),
              );
            }
          }
          if (this.voucherGroup().type === 'activate') {
            this.activateVoucherForm.addControl(
              'number',
              new UntypedFormControl('', Validators.required),
            );
          }

          if (!this.voucherGroup().dontRequestAddress) {
            this.activateVoucherForm.addControl(
              'postal',
              new UntypedFormControl('', [CustomValidators.postal, Validators.required]),
            );
            this.activateVoucherForm.addControl(
              'houseNumber',
              new UntypedFormControl('', [
                Validators.required,
                CustomValidators.numberInput(false, false, 0),
              ]),
            );
            this.activateVoucherForm.addControl(
              'houseNumberAddition',
              new UntypedFormControl('', [Validators.pattern(/^[a-zA-Z0-9]*$/)]),
            );
          }
          if (this.voucherGroup().cashback) {
            this.activateVoucherForm.addControl(
              'ibanNumber',
              new UntypedFormControl('', [Validators.required, CustomValidators.iban]),
            );
            this.activateVoucherForm.addControl(
              'bankAccountName',
              new UntypedFormControl('', [Validators.required]),
            );
          }
          this.termsUrl.set(
            getTermsUrl('general', this.voucherGroup()?.termsURL, this.township()?.termsUrl),
          );
          if (this.voucherGroup().questionnaire) {
            this.questionnaireEnabled.set(true);
            Object.keys(this.questionnaireData).forEach((key) => {
              this.handleQuestionnaireQuestion(key, this.questionnaireForm.value[key]);
            });
          }
          if (this.firstTime) {
            this.firstTime = false;
            if (this.preFilledData) {
              console.log('preFilledData:', this.preFilledData);
              const prefilledFormValues = { ...this.preFilledData } as any;
              prefilledFormValues.emailConfirm = this.preFilledData.email;
              prefilledFormValues.identification = this.preFilledData.name;
              this.activateVoucherForm.patchValue(prefilledFormValues);
              if (this.preFilledData.postal) {
                this.activateVoucherForm.controls.postal?.disable();
                this.activateVoucherForm.controls.houseNumber?.disable();
                this.activateVoucherForm.controls.houseNumberAddition?.disable();
              }
              if (this.preFilledData.email) {
                this.activateVoucherForm.controls.email?.disable();
                this.activateVoucherForm.controls.emailConfirm?.disable();
              }
              if (this.preFilledData.questionnaire) {
                this.questionnaireForm.patchValue(this.preFilledData.questionnaire);
              }
            }
          }
        }
      });
    } else {
      this.activateVoucherForm.addControl(
        'number',
        new UntypedFormControl('', Validators.required),
      );
      this.activateVoucherForm.addControl('name', new UntypedFormControl(''));
      this.activateVoucherForm.addControl(
        'postal',
        new UntypedFormControl('', Validators.required),
      );
      this.activateVoucherForm.addControl(
        'houseNumber',
        new UntypedFormControl('', [Validators.required, number({ min: 1 })]),
      );
      this.activateVoucherForm.addControl(
        'houseNumberAddition',
        new UntypedFormControl('', [Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      );
      this.activateVoucherForm.addControl(
        'phone',
        new UntypedFormControl('06', [
          Validators.required,
          CustomValidators.phone,
          startsWith('06'),
        ]),
      );
      this.termsUrl.set(getTermsUrl('general', undefined, this.township()?.termsUrl));
    }
    this.loaded.set(true);
  }
  async submit(verificationCode?: string) {
    if (
      !this.awaitingResponse() &&
      this.activateVoucherForm.valid &&
      (!this.questionnaireEnabled() || this.questionnaireForm.valid)
    ) {
      this.awaitingResponse.set(true);
      let questionnaireAnswers = null;
      if (this.questionnaireEnabled()) {
        questionnaireAnswers = this.questionnaireForm.value ?? {};
        Object.keys(questionnaireAnswers).forEach((key) => {
          if (!key.includes('Input')) {
            questionnaireAnswers[key] =
              this.questionnaireData[key].answers[questionnaireAnswers[key]];
            if (questionnaireAnswers[`${key}Input`]) {
              questionnaireAnswers[key] = questionnaireAnswers[`${key}Input`];
              delete questionnaireAnswers[`${key}Input`];
            }
          }
        });
      }
      const form = this.activateVoucherForm.getRawValue();
      this.activateVoucherForm.disable();
      this.questionnaireForm.disable();
      const email = form.email as string;
      const emailConfirm = form.emailConfirm as string;
      if (email !== emailConfirm) {
        this.awaitingResponse.set(false);
        this.activateVoucherForm.enable();
        this.questionnaireForm.enable();
        this.snackBar.open(this.getTranslation('email-dont-match'), '', {
          duration: 5000,
        });
        return;
      }
      let postal: string;
      let houseNumber: string;
      let houseNumberAddition: string;
      if (!this.voucherGroup()?.dontRequestAddress) {
        postal = form.postal as string;
        postal = postal.toUpperCase().replace(/\s/g, '');
        houseNumber = form.houseNumber.toString();
        houseNumberAddition = form.houseNumberAddition as string;
      }

      try {
        const recaptchaToken =
          await this.publicFunctionsService.getRecaptchaToken('activate_voucher');

        const body = {
          townshipId: this.townshipId(),
          voucherGroupId: this.voucherGroupId(),
          // ambitionLevel: this.inputData?.ambitionLevel,
          email,
          postal: postal ? postal : null,
          houseNumber: houseNumber ? houseNumber : null,
          houseNumberAddition:
            houseNumberAddition && houseNumberAddition.length
              ? houseNumberAddition.toLowerCase()
              : null,
          code: form.number ?? null,
          name: form.name ?? null,
          phone: form.phone ?? null,
          verificationCode: verificationCode ? verificationCode : null,
          identification: form.identification ?? null,
          ibanNumber: form.ibanNumber ? form.ibanNumber.toUpperCase().replace(/\s/g, '') : null,
          bankAccountName: form.bankAccountName ?? null,
          questionnaireAnswers: questionnaireAnswers,
          recaptchaToken,
        };

        console.log(body);

        if (this.data?.measures) {
          body['measures'] = this.data.measures;
        }
        const callable = httpsCallable(this.functions, `httpVoucher`);
        const result = await callable(body);
        if (result.data['status']) {
          console.log('result', result);
          if (
            result.data['status'] === 'verification_code_sent' ||
            result.data['status'] === 'verification_code_incorrect'
          ) {
            // Should show verification screen
            if (result.data['status'] === 'verification_code_incorrect') {
              this.getTranslation(result.data['status']);
            }
            this.verifyPhone(form.phone);
          } else if (result.data['status'] === 'success') {
            this.activateVoucherForm.reset();
            this.questionnaireForm.reset();
            this.dialog.open(SucceedActivationComponent, {
              width: '350px',
              data: { voucherGroup: result.data['voucherGroup'] },
              disableClose: true,
            });
          } else {
            this.getTranslation(result.data['status']);
          }
        }
      } catch (e) {
        console.error(e);
        this.getTranslation('something-went-wrong');
      } finally {
        this.awaitingResponse.set(false);
        this.activateVoucherForm.enable();
        this.questionnaireForm.enable();
      }
    }
  }

  async sendPreFilledForm() {
    if (!this.uniqueTownshipName()) {
      return this.snackBar.open(
        'Voorbereiden van formulieren werkt alleen wanneer de unieke naam ingesteld is.',
        '',
        {
          duration: 5000,
        },
      );
    }
    const form = this.activateVoucherForm.getRawValue();
    this.activateVoucherForm.disable();
    if (form.email !== form.emailConfirm) {
      this.awaitingResponse.set(false);
      this.activateVoucherForm.enable();
      return this.snackBar.open(this.getTranslation('email-dont-match'), '', {
        duration: 5000,
      });
    }
    let questionnaire = null;
    if (this.questionnaireEnabled()) {
      let linkString: string = '';
      for (const key of Object.keys(this.questionnaireForm.value)) {
        if (this.questionnaireForm.value[key]) {
          if (linkString !== '') {
            linkString = linkString + ',';
          }
          linkString =
            linkString +
            `"${key}":${typeof this.questionnaireForm.value[key] !== 'number' ? `"${this.questionnaireForm.value[key]}"` : this.questionnaireForm.value[key]}`;
        }
      }
      if (linkString !== '') {
        questionnaire = `{${linkString}}`;
      }
    }
    this.questionnaireForm.disable();
    const iframeUrl = `${window.location.protocol}//${window.location.host}`;
    const activateVoucherUrl = `${iframeUrl}/activate/${this.uniqueTownshipName()}/${this.voucherGroupId()}`;
    let customParams = `email=${form.email}`;
    if (form.postal) {
      customParams = `${customParams}&postal=${form.postal}`;
    }
    if (form.houseNumber) {
      customParams = `${customParams}&houseNumber=${form.houseNumber}`;
    }
    if (form.houseNumberAddition) {
      customParams = `${customParams}&houseNumberAddition=${form.houseNumberAddition}`;
    }
    if (form.phone) {
      customParams = `${customParams}&phone=${form.phone}`;
    }
    let name = '';
    if (form.name || form.identification || form.bankAccountName) {
      name = form.name ?? form.identification ?? form.bankAccountName;
      customParams = `${customParams}&name=${name}`;
    }
    if (form.ibanNumber) {
      customParams = `${customParams}&ibanNumber=${form.ibanNumber}`;
    }
    if (form.bankAccountName) {
      customParams = `${customParams}&bankAccountName=${form.bankAccountName}`;
    }
    if (questionnaire) {
      customParams = `${customParams}&questionnaire=${questionnaire ?? ''}`;
    }
    console.log(questionnaire);
    console.log('activateVoucherUrl', activateVoucherUrl);
    const dataObj: MailDataObj = {
      orgData: {
        townshipId: this.townshipId(),
        voucherGroup: this.voucherGroup(),
      },
      mailData: {
        mailType: 'preFilledActivation',
        downloadUrl: `${activateVoucherUrl}/${encodeRouteParam(customParams)}`,
      },
      userData: { email: [form.email], name: name },
    };
    try {
      this.awaitingResponse.set(true);
      const headers = await this.publicFunctionsService.getSecurityHeaders('send_mail');
      const sendMailRes = (await lastValueFrom(
        this.http.post(
          `${this.env.functionsUrl}/httpSendMail`,
          {
            data: dataObj,
          },
          { headers },
        ),
      )) as any;
      console.log('sendMailRes', sendMailRes);
      if (sendMailRes.result?.status != 'success') {
        throw Error(sendMailRes.result?.status);
      }
      this.prefilledFormSent.set(true);
      this.awaitingResponse.set(false);
      this.snackBar.open('Het aanvraagformulier is successvol verstuurd.', 'X', {
        duration: 5000,
      });
    } catch (e) {
      this.awaitingResponse.set(false);
      this.activateVoucherForm.enable();
      this.questionnaireForm.enable();
      console.error(e);
      this.snackBar.open('Er ging iets mis bij het verzenden van de bon.', 'X', {
        duration: 5000,
      });
    }
  }

  handleQuestionnaireQuestion(key: string, value?: string) {
    if (
      this.questionnaireData[key].answers[value] == this.questionnaireData[key].answers.slice(-1)[0]
    ) {
      if (!this.questionnaireForm.controls[`${key}Input`]) {
        this.questionnaireForm.addControl(
          `${key}Input`,
          new UntypedFormControl('', !this.isPreperationForm() ? Validators.required : null),
        );
      }
    } else if (this.questionnaireForm.controls[`${key}Input`]) {
      this.questionnaireForm.removeControl(`${key}Input`);
    }
  }

  getTranslation(objName: string) {
    let message = this.translate.instant(`snackbar-messages.${objName}`) as string;
    console.log('message', message);
    if (message.includes('snackbar-messages.')) {
      // translation not found, get oops error instead
      message = this.translate.instant(`snackbar-messages.something-went-wrong`) as string;
    }
    this.snackBar.open(message, 'X', {
      duration: 12000,
    });
    return message;
  }

  setHighContrast() {
    this.highContrast.set(!this.highContrast());
    localStorage.setItem('highContrast', this.highContrast() ? '1' : '0');
  }
}
export function startsWith(startsWithVal): ValidatorFn {
  console.log('startsWithVal', startsWithVal);

  return (control: AbstractControl): { [key: string]: any } => {
    const value = control.value ? control.value : '';
    const isStarting = 0 === value.indexOf(startsWithVal);
    return isStarting ? null : { startsWith: true };
  };
}
export function number(prms: { min?: number; max?: number } = {}): ValidatorFn {
  return (control: UntypedFormControl): { [key: string]: any } => {
    const val: number = control.value;
    if (!val && val !== 0) {
      return null;
    }
    if (isNaN(val) || /\D/.test(val.toString())) {
      return { number: true };
    } else if (!isNaN(prms.min) && !isNaN(prms.max)) {
      return val < prms.min || val > prms.max ? { number: true } : null;
    } else if (!isNaN(prms.min)) {
      return val < prms.min ? { number: true } : null;
    } else if (!isNaN(prms.max)) {
      return val > prms.max ? { number: true } : null;
    } else {
      return null;
    }
  };
}
